import { createDateColumn, createSortableColumnId, createTextColumn, useGenericListConfig } from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import { format } from 'date-fns';

import type { StatementListItem } from '../types/Statements.types';

const columnIdNames = {
  billingPeriod: 'billingPeriod',
  newBalance: createSortableColumnId('', 'newBalanceSortDirection'),
  dueDate: 'dueDate',
  minimumPaymentDue: 'minimumPaymentDue',
  graceActive: 'graceActive',
  previousBalancePaidInFull: 'previousBalancePaidInFull',
  feesCharged: 'feesCharged',
  lateFeeApplied: 'lateFeeApplied',
  dueDateProcessedAt: createSortableColumnId('', 'dueDateProcessedAtSortDirection'),
  hasPdf: 'hasPdf',
};

const formatBillingPeriod = (row: StatementListItem) => {
  const start = row.billingPeriodStart ? format(new Date(row.billingPeriodStart), 'MMM d') : '';
  const end = row.billingPeriodEnd ? format(new Date(row.billingPeriodEnd), 'MMM d, yyyy') : '';

  return `${start} - ${end}`;
};

export const useStatementsListConfig = () => {
  const columns = [
    createTextColumn(columnIdNames.billingPeriod, 'Billing Period', formatBillingPeriod),
    createTextColumn(
      columnIdNames.newBalance,
      ({ column }) => <DataTableColumnHeader column={column} title="Balance" />,
      (row: StatementListItem) => row.newBalance,
    ),
    createTextColumn(columnIdNames.dueDate, 'Due Date', (row: StatementListItem) => row.dueDate),
    createTextColumn(columnIdNames.minimumPaymentDue, 'Min Payment', (row: StatementListItem) => row.minimumPaymentDue),
    createTextColumn(columnIdNames.graceActive, 'Grace', (row: StatementListItem) => (row.graceActive ? '✓' : '—')),
    createTextColumn(columnIdNames.previousBalancePaidInFull, 'Paid in Full', (row: StatementListItem) =>
      row.previousBalancePaidInFull ? '✓' : '—',
    ),
    createTextColumn(columnIdNames.feesCharged, 'Fees', (row: StatementListItem) => row.feesCharged),
    createTextColumn(columnIdNames.lateFeeApplied, 'Late Fee', (row: StatementListItem) => row.lateFeeApplied ?? '—'),
    createDateColumn(
      columnIdNames.dueDateProcessedAt,
      ({ column }) => <DataTableColumnHeader column={column} title="Due Date Processed At" />,
      (row: StatementListItem) => row.dueDateProcessedAt,
    ),
    createTextColumn(columnIdNames.hasPdf, 'PDF', (row: StatementListItem) => (row.hasPdf ? '✓' : '—')),
  ];

  return useGenericListConfig<StatementListItem>({
    columns,
    pinnedColumns: ['actions'],
  });
};
